import React, { useState, useEffect } from 'react';
import initialUsers from './mock/users';
import LoginForm from './components/Auth/LoginForm';
import MainLayout from './components/Layout/MainLayout';
import AdminLayout from './components/Admin/AdminLayout';
import AdminDashboard from './components/Admin/AdminDashboard';
import UserDashboard from './components/User/UserDashboard';
import RegistrationForm from './components/Auth/RegistrationForm';
import GuestScanViewer from './components/Guest/GuestScanViewer';
import ForgotPasswordForm from './components/Auth/ForgotPasswordForm';
import { getStorage, setStorage } from './utils/storage';

const App = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [selectedScan, setSelectedScan] = useState(null);
  const [allUsers, setAllUsers] = useState(() => getStorage('users', initialUsers));
  const [currentPage, setCurrentPage] = useState('login');
  const [sharedScanId, setSharedScanId] = useState(null);

  // Save users to localStorage whenever allUsers changes
  useEffect(() => {
    setStorage('users', allUsers);
  }, [allUsers]);

  // Check URL for shared scan link on initial load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const scanId = params.get('scanId');
    if (scanId) {
      setSharedScanId(parseInt(scanId));
      setCurrentPage('login');
    } else {
      setCurrentPage('login');
    }
  }, []);


  const handleLogin = (username, password) => {
    const user = allUsers.find(
      (u) => (u.username === username || u.email === username) && u.password === password
    );
    if (user) {
      setCurrentUser(user);
      // Increment login count
      setAllUsers(prevUsers => prevUsers.map(u => 
        u.id === user.id ? { ...u, loginCount: (u.loginCount || 0) + 1 } : u
      ));

      // If there was a shared scan ID, try to find and show it after login
      if (sharedScanId) {
        let foundScan = null;
        let foundBoat = null;
        for (const user of allUsers) {
          for (const boat of user.boats) {
            const scan = boat.scans.find(s => s.id === sharedScanId);
            if (scan) {
              foundScan = scan;
              foundBoat = boat;
              break;
            }
          }
          if (foundScan) break;
        }
        if (foundScan) {
          setSelectedScan(foundScan);
          setSelectedBoat(foundBoat);
          // Navigate to the user dashboard view for the scan, logic inside UserDashboard handles guest view
          setCurrentPage('userScanView');
        } else {
           // If scan not found after login (shouldn't happen with valid ID), go to dashboard
           setCurrentPage('dashboard');
        }
        setSharedScanId(null); // Clear the shared scan ID
        window.history.replaceState({}, document.title, window.location.pathname); // Clean URL
      } else {
         setCurrentPage('dashboard'); // Go to regular dashboard if no shared scan
      }
    } else {
      alert('Credenciales incorrectas');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setSelectedBoat(null);
    setSelectedScan(null);
    setCurrentPage('login');
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const handleRegister = (email, password, username) => {
     // Check if email or username already exists
    const userExists = allUsers.some(user => user.email === email || user.username === username);
    if (userExists) {
      alert('El correo electrónico o nombre de usuario ya existe.');
      return;
    }

    const newUser = {
      id: Date.now(),
      username: username,
      password: password,
      role: 'guest',
      email: email,
      boats: [],
      loginCount: 0
    };
    setAllUsers(prevUsers => [...prevUsers, newUser]);
    alert('Registro exitoso. Ahora puedes iniciar sesión.');
    setCurrentPage('login');
  };

  const handleForgotPassword = (email) => {
    // Simulate sending reset link
    alert(`Simulando envío de enlace de recuperación a ${email}.`);
    setCurrentPage('login'); // Go back to login after simulation
  };


  const handleAddScan = (userId, boatId, newScan) => {
    setAllUsers(prevUsers => {
      return prevUsers.map(user => {
        if (user.id === userId) {
          const updatedBoats = user.boats.map(boat => {
            if (boat.id === boatId) {
              return {
                ...boat,
                scans: [...boat.scans, {
                  id: Date.now(),
                  ...newScan,
                }]
              };
            }
            return boat;
          });
          return { ...user, boats: updatedBoats };
        }
        return user;
      });
    });
  };

  const handleCreateUser = (newUserData) => {
    const userExists = allUsers.some(user => user.username === newUserData.username || user.email === newUserData.email);
    if (userExists) {
      alert('El nombre de usuario o correo electrónico ya existe.');
      return;
    }

    setAllUsers(prevUsers => [
      ...prevUsers,
      {
        id: Date.now(),
        username: newUserData.username,
        password: newUserData.password,
        role: newUserData.role,
        email: newUserData.email,
        boats: [],
        loginCount: 0
      }
    ]);
  };

  const handleCreateBoat = (userId, newBoatData) => {
    setAllUsers(prevUsers => {
      return prevUsers.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            boats: [...user.boats, {
              id: Date.now(),
              ...newBoatData,
              viewCount: 0
            }]
          };
        }
        return user;
      });
    });
  };

  const handleDeleteUser = (userId) => {
    setAllUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
  };

  const handleDeleteBoat = (userId, boatId) => {
    setAllUsers(prevUsers => {
      return prevUsers.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            boats: user.boats.filter(boat => boat.id !== boatId)
          };
        }
        return user;
      });
    });
  };

  const handleEditScan = (userId, boatId, scanId, updatedScanData) => {
    setAllUsers(prevUsers => {
      return prevUsers.map(user => {
        if (user.id === userId) {
          const updatedBoats = user.boats.map(boat => {
            if (boat.id === boatId) {
              return {
                ...boat,
                scans: boat.scans.map(scan => {
                  if (scan.id === scanId) {
                    return {
                      ...scan,
                      ...updatedScanData
                    };
                  }
                  return scan;
                })
              };
            }
            return boat;
          });
          return { ...user, boats: updatedBoats };
        }
        return user;
      });
    });
  };

  const handleDeleteDocument = (userId, boatId, scanId, documentId) => {
    setAllUsers(prevUsers => {
      return prevUsers.map(user => {
        if (user.id === userId) {
          const updatedBoats = user.boats.map(boat => {
            if (boat.id === boatId) {
              return {
                ...boat,
                scans: boat.scans.map(scan => {
                  if (scan.id === scanId) {
                    return {
                      ...scan,
                      documents: scan.documents.filter(doc => doc.id !== documentId)
                    };
                  }
                  return scan;
                })
              };
            }
            return boat;
          });
          return { ...user, boats: updatedBoats };
        }
        return user;
      });
    });
  };

  const handleAddDocumentToScan = (userId, boatId, scanId, newDocumentData) => {
    setAllUsers(prevUsers => {
      return prevUsers.map(user => {
        if (user.id === userId) {
          const updatedBoats = user.boats.map(boat => {
            if (boat.id === boatId) {
              return {
                ...boat,
                scans: boat.scans.map(scan => {
                  if (scan.id === scanId) {
                    return {
                      ...scan,
                      documents: [...scan.documents, { id: Date.now(), ...newDocumentData }]
                    };
                  }
                  return scan;
                })
              };
            }
            return boat;
          });
          return { ...user, boats: updatedBoats };
        }
        return user;
      });
    });
  };

  const handleChangeUserRole = (userId, newRole) => {
    setAllUsers(prevUsers => {
      return prevUsers.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            role: newRole
          };
        }
        return user;
      });
    });
  };

  const handleEditUserEmail = (userId, newEmail) => {
     setAllUsers(prevUsers => {
      return prevUsers.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            email: newEmail
          };
        }
        return user;
      });
    });
  };

  const handleBoatViewed = (userId, boatId) => {
     setAllUsers(prevUsers => {
      return prevUsers.map(user => {
        if (user.id === userId) {
          const updatedBoats = user.boats.map(boat => {
            if (boat.id === boatId) {
              return {
                ...boat,
                viewCount: (boat.viewCount || 0) + 1 // Increment view count
              };
            }
            return boat;
          });
          return { ...user, boats: updatedBoats };
        }
        return user;
      });
    });
  };


  // Render logic based on currentPage state
  if (currentPage === 'login') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <LoginForm onLogin={handleLogin} onForgotPassword={() => setCurrentPage('forgotPassword')} />
        <button
          onClick={() => setCurrentPage('register')}
          className="absolute bottom-8 text-blue-600 hover:text-blue-800"
        >
          ¿No tienes cuenta? Regístrate aquí
        </button>
      </div>
    );
  }

  if (currentPage === 'register') {
     return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <RegistrationForm onRegister={handleRegister} />
         <button
          onClick={() => setCurrentPage('login')}
          className="absolute bottom-8 text-blue-600 hover:text-blue-800"
        >
          ¿Ya tienes cuenta? Inicia sesión
        </button>
      </div>
    );
  }

  if (currentPage === 'forgotPassword') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <ForgotPasswordForm onResetPassword={handleForgotPassword} onBackToLogin={() => setCurrentPage('login')} />
      </div>
    );
  }


  // If a sharedScanId exists AND the user is logged in AND the user is a guest, show the guest view
  if (currentPage === 'guestScan' && currentUser && currentUser.role === 'guest' && selectedScan) {
     return (
       <MainLayout onLogout={handleLogout}>
         <GuestScanViewer scan={selectedScan} />
       </MainLayout>
    );
  }

  // If a sharedScanId exists AND the user is logged in AND the user is NOT a guest, show the full user view
   if (currentPage === 'userScanView' && currentUser && currentUser.role !== 'guest' && selectedScan) {
     return (
      <MainLayout onLogout={handleLogout}>
        <UserDashboard
          user={currentUser}
          selectedBoat={selectedBoat}
          setSelectedBoat={setSelectedBoat}
          selectedScan={selectedScan}
          setSelectedScan={setSelectedScan}
          onBoatViewed={handleBoatViewed} // Pass the function
        />
      </MainLayout>
    );
  }


  // If user is logged in and NOT viewing a shared scan, show their respective dashboard
  if (currentUser && !sharedScanId) {
      if (currentUser.role === 'admin' || currentUser.role === 'masteradmin') {
        return (
          <AdminLayout onLogout={handleLogout}>
            <AdminDashboard 
              users={allUsers}
              onAddScan={handleAddScan}
              onCreateUser={handleCreateUser}
              onCreateBoat={handleCreateBoat}
              onDeleteUser={handleDeleteUser}
              onDeleteBoat={handleDeleteBoat}
              onEditScan={handleEditScan}
              onDeleteDocument={handleDeleteDocument}
              onAddDocumentToScan={handleAddDocumentToScan}
              currentUserRole={currentUser.role}
              onChangeUserRole={handleChangeUserRole}
              onEditUserEmail={handleEditUserEmail}
            />
          </AdminLayout>
        );
      }

      if (currentUser.role === 'user' || currentUser.role === 'guest') {
         return (
          <MainLayout onLogout={handleLogout}>
            <UserDashboard
              user={currentUser}
              selectedBoat={selectedBoat}
              setSelectedBoat={setSelectedBoat}
              selectedScan={selectedScan}
              setSelectedScan={setSelectedScan}
              onBoatViewed={handleBoatViewed} // Pass the function
            />
          </MainLayout>
        );
      }
  }

  // Fallback to login if somehow not logged in or on an unknown page
  return (
     <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <LoginForm onLogin={handleLogin} onForgotPassword={() => setCurrentPage('forgotPassword')} />
         <button
          onClick={() => setCurrentPage('register')}
          className="absolute bottom-8 text-blue-600 hover:text-blue-800"
        >
          ¿No tienes cuenta? Regístrate aquí
        </button>
      </div>
  );
};

export default App;

// DONE